import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { LIST_ROW_HEIGHTS } from '../../../../constants/list_row_heights';
import {
  closeInnerPopup,
  closeTopLevelPopup,
  openInnerPopup,
  openTopLevelPopup,
} from '../../../../data/providers/popupsProvider';
import {
  IScoringState,
  scoringProvider,
} from '../../../../data/providers/scoringProvider';
import { IComparisonPopulationSetting } from '../../../../data/providers/settings/comparisonPopulationSettingsProvider';
import {
  IAggregated,
  IMetric,
  ISelectedLabelKey,
  ITest,
  Level,
} from '../../../../data/types';
import { setSelectedMetricMean } from '../../../../data/writers/settings';
import { Column } from '../../../layout/Column';
import { List } from '../../../shared/List/List';
import { NoResults } from '../../../shared/NoResults';
import { Card } from '../../../shared/card/Card';
import { CardBody } from '../../../shared/card/CardBody';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { Notable } from '../../../shared/pdf/Notable';
import { MetricHistoryPopup } from '../metric_history/MetricHistoryPopup';
import { ExportPopup } from './ExportPopup';
import { Score } from './Score';
import { ScoringHeader } from './ScoringHeader';

interface IProps extends RouteComponentProps {
  metrics?: IMetric[];
  test?: ITest;
  style?: React.CSSProperties;
  isPdf?: boolean;
  level?: Level;
}

export const Scoring = withRouter((props: IProps): React.ReactElement => {
  const listShape = [2, 1, 1, 1, 1];
  const { metrics = [], test } = props;
  const [loading, setLoading] = React.useState(true);
  const [aggregates, setAggregates] = React.useState<IAggregated>();
  const [settings, setSettings] =
    React.useState<IComparisonPopulationSetting>();

  const [metricFilter, setMetricFilter] = React.useState<string>();

  /**
   * @description fetches the aggregates when test to compare changes or the population to compare against changes
   */
  React.useEffect((): (() => void) => {
    if (!test) return;
    setLoading(true);
    const subscriber = scoringProvider(test).subscribe(
      (evt: IScoringState): void => {
        setAggregates({
          eventId: evt.aggregates.eventId,
          aggregate: evt.aggregates.combined,
        });
        setSettings(evt.aggregates.settings);
        setLoading(false);
      }
    );
    return (): void => subscriber?.unsubscribe();
  }, [test?.athlete?.id, test?.queryHash, test?.id]);

  const displayableMetrics = React.useMemo((): IMetric[] => {
    if (metricFilter && metricFilter.length > 0) {
      return props.metrics.filter((metric: IMetric): boolean => {
        /// convert backslash to pipe for asymmetry metrics
        /// users have a hard time typing pipe
        return metric.label
          .toLowerCase()
          .includes(metricFilter.replace('/', '|').toLowerCase());
      });
    } else {
      return props.metrics;
    }
  }, [metricFilter, JSON.stringify(props.metrics)]);

  /**
   * @description handles user click on a specific metric row
   * @param selected
   */
  const metricClickHandler = (selected: ISelectedLabelKey): void => {
    setSelectedMetricMean(selected);
    if (props.level === 2) {
      openTopLevelPopup(
        <MetricHistoryPopup
          test={props.test}
          metric={selected}
          onClose={closeTopLevelPopup}
          level={props.level}
        />
      );
    } else {
      openInnerPopup(
        <MetricHistoryPopup
          test={props.test}
          metric={selected}
          onClose={closeInnerPopup(props.history)}
          level={props.level}
        />,
        props.history,
        'metric-history'
      );
    }
  };

  /**
   * @description renders each metric row
   * @param param0
   */
  const metricRenderer = ({
    index,
    key,
    style,
  }: {
    index: number;
    key: string;
    style: React.CSSProperties;
  }): React.ReactElement => {
    const metric = displayableMetrics[index];
    return (
      <Score
        aggregate={aggregates?.aggregate?.data?.[metric?.id]}
        listShape={listShape}
        metric={metric}
        key={key}
        style={style}
        loading={loading}
        scale={settings?.zScorePreference}
        onMetricClick={metricClickHandler}
        level={props.level}
      />
    );
  };

  const openExport = (): void => {
    if (props.level === 2) {
      openTopLevelPopup(
        <ExportPopup
          closeDialog={closeTopLevelPopup}
          periodAggregates={aggregates}
          selectedPeriod={settings?.dateRangeType?.value}
          metrics={metrics}
          test={test}
          startDate={settings?.dateRangeValues?.from}
          endDate={settings?.dateRangeValues?.to}
          useZPercentile={settings?.zScorePreference}
          level={2}
        />
      );
    } else {
      openInnerPopup(
        <ExportPopup
          closeDialog={closeInnerPopup(props.history)}
          periodAggregates={aggregates}
          selectedPeriod={settings?.dateRangeType?.value}
          metrics={metrics}
          test={test}
          startDate={settings?.dateRangeValues?.from}
          endDate={settings?.dateRangeValues?.to}
          useZPercentile={settings?.zScorePreference}
        />,
        props.history,
        'export-test'
      );
    }
  };

  const additionalStyles = props.isPdf
    ? { overflow: 'visible', height: 'auto' }
    : {};
  return (
    <ResultsCard
      style={{
        ...props.style,
        ...additionalStyles,
      }}
    >
      {test ? (
        <Column>
          <ScoringHeader
            periodAggregates={aggregates}
            settings={settings}
            loading={loading}
            listShape={listShape}
            level={props.level}
            onFilterChange={setMetricFilter}
            filter={metricFilter}
            isPdf={props.isPdf}
            openExport={openExport}
            setLoading={setLoading}
          />

          {!props.isPdf ? (
            <CardBody
              withoutFooter
              style={{
                minHeight: 'var(--graph-min-height)',
                backgroundColor: 'var(--color-list-background)',
              }}
            >
              {loading && <LinearLoading />}
              {displayableMetrics && displayableMetrics.length > 0 ? (
                <List
                  listLength={displayableMetrics.length}
                  rowRenderer={metricRenderer}
                  rowHeight={LIST_ROW_HEIGHTS.large}
                />
              ) : metricFilter?.length ? (
                <NoResults
                  message='No Metrics Found'
                  filterApplied
                  clearFilter={(): void => setMetricFilter('')}
                />
              ) : (
                <NoResults message='No Test Data' />
              )}
            </CardBody>
          ) : (
            props.metrics.map(
              (metric: IMetric, index: number): React.ReactElement => {
                return (
                  <Notable
                    pdf={props.isPdf}
                    style={{ padding: '0px 10px' }}
                    key={metric.id}
                    level={props.level}
                  >
                    <Score
                      listShape={listShape}
                      aggregate={aggregates?.aggregate?.data?.[metric?.id]}
                      scale={settings?.zScorePreference}
                      metric={metric}
                      key={index}
                      style={{ height: '80px' }}
                      loading={loading}
                      onMetricClick={() => {}}
                    />
                  </Notable>
                );
              }
            )
          )}
        </Column>
      ) : (
        <LinearLoading />
      )}
    </ResultsCard>
  );
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ResultsCard = styled(Card as any)`
  height: 100%;
  padding: 0px;
  display: flex;
  margin-top: var(--grid-gap);
`;
